<template>
  <div class="bl-wrapper">
    <vue-element-loading :active="submiting" />

    <!-- ═══════════════ MOBILE LAYOUT ═══════════════ -->
    <div class="bl-mobile d-flex d-md-none">
      <!-- Hero top -->
      <div class="bl-m-hero">
        <!-- Language toggle -->
        <button class="bl-lang-btn" @click="toggleLang">
          <v-icon size="13" color="rgba(255,255,255,0.9)">mdi-translate</v-icon>
          <span>{{ currentLang }}</span>
        </button>

        <!-- Logo + name -->
        <div class="bl-m-logo-wrap">
          <div class="bl-m-logo-ring">
            <v-img
              :src="require('@/assets/images/logos/logo_4_small_bustracker.png')"
              max-height="48px"
              max-width="48px"
              contain
            />
          </div>
          <h1 class="bl-m-app-name">{{ systemName }}</h1>
          <p class="bl-m-tagline">
            {{
              currentLang === 'SQ'
                ? 'Gjurmim live · Nxënës · Shkolla'
                : 'Live tracking · Pupils · Schools'
            }}
          </p>
        </div>
      </div>

      <!-- Form card (overlaps hero) -->
      <div class="bl-m-card">
        <div class="bl-m-card-handle"></div>

        <div class="bl-m-greeting">
          <h2 class="bl-m-title">{{ currentLang === 'SQ' ? 'Mirë se vini!' : 'Welcome back!' }}</h2>
          <p class="bl-m-subtitle">{{ currentLang === 'SQ' ? 'Hyni në llogarinë tuaj' : 'Sign in to your account' }}</p>
        </div>

        <v-form ref="form" v-model="valid" lazy-validation class="bl-m-form">
          <div class="bl-field-group">
            <label class="bl-label">{{ currentLang === 'SQ' ? 'Email ose Telefon' : 'Email or Phone' }}</label>
            <v-text-field
              v-model="email"
              outlined
              dense
              :placeholder="$vuetify.breakpoint.smAndDown ? 'email / 044... / +383...' : 'email@shembull.com  ose  044123456  ose  +38344123456'"
              required
              :rules="emailRules"
              hide-details="auto"
              prepend-inner-icon="mdi-account-outline"
              class="bl-field"
            ></v-text-field>
          </div>

          <div class="bl-field-group">
            <label class="bl-label">{{ currentLang === 'SQ' ? 'Fjalëkalimi' : 'Password' }}</label>
            <v-text-field
              v-model="password"
              outlined
              dense
              :type="isPasswordVisible ? 'text' : 'password'"
              placeholder="············"
              :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
              required
              :rules="passRules"
              hide-details="auto"
              prepend-inner-icon="mdi-lock-outline"
              class="bl-field"
              @click:append="isPasswordVisible = !isPasswordVisible"
            ></v-text-field>
          </div>

          <div class="bl-meta">
            <v-checkbox
              v-model="rememberMe"
              :label="currentLang === 'SQ' ? 'Më mbaj mend' : 'Remember me'"
              hide-details dense class="bl-checkbox"
            />
            <router-link to="/forgot-password" class="bl-forgot">
              {{ currentLang === 'SQ' ? 'Harruat fjalëkalimin?' : 'Forgot password?' }}
            </router-link>
          </div>

          <v-btn block depressed large class="bl-btn" @click="login">
            <v-icon left size="18">mdi-login</v-icon>
            {{ currentLang === 'SQ' ? 'Hyr' : 'Sign In' }}
          </v-btn>
        </v-form>

        <!-- Demoja hapet ne gjuhen qe eshte zgjedhur ketu. -->
        <router-link v-if="demoEnabled" :to="demoLink" class="bl-demo-btn">
          <span class="bl-demo-icon">&#128269;</span>
          {{ currentLang === 'SQ' ? 'Eksploro versionin demo' : 'Explore the demo version' }}
        </router-link>

        <a href="/app/" class="bl-driver-btn">
          <span class="bl-demo-icon">&#128241;</span>
          <span>
            {{ currentLang === 'SQ' ? 'Shkarko aplikacionin për shoferë' : 'Download the driver app' }}
            <small>{{ currentLang === 'SQ' ? 'Vetëm për Android' : 'Android only' }}</small>
          </span>
        </a>

        <p class="bl-m-footer">&copy; {{ new Date().getFullYear() }} {{ systemName }}</p>
        <div class="bl-m-links">
          <router-link to="/privacy" class="bl-link">{{ currentLang === 'SQ' ? 'Politika e Privatësisë' : 'Privacy Policy' }}</router-link>
          <span class="bl-link-sep">·</span>
          <router-link to="/terms" class="bl-link">{{ currentLang === 'SQ' ? 'Kushtet e Shërbimit' : 'Terms & Conditions' }}</router-link>
        </div>
      </div>
    </div>

    <!-- ═══════════════ DESKTOP LAYOUT ═══════════════ -->
    <div class="bl-desktop d-none d-md-flex">
      <!-- Left branding panel -->
      <div class="bl-d-left">
        <div class="bl-d-left-inner">
          <div class="bl-d-brand">
            <v-img
              :src="require('@/assets/images/logos/logo_4_small_bustracker.png')"
              max-height="54px" max-width="54px" contain
            />
            <span class="bl-d-brand-name">{{ systemName }}</span>
          </div>

          <svg viewBox="0 0 420 240" fill="none" xmlns="http://www.w3.org/2000/svg" class="bl-d-svg">
            <rect x="0" y="195" width="420" height="45" rx="4" fill="rgba(255,255,255,0.07)"/>
            <rect x="40" y="214" width="60" height="6" rx="3" fill="rgba(255,255,255,0.2)"/>
            <rect x="180" y="214" width="60" height="6" rx="3" fill="rgba(255,255,255,0.2)"/>
            <rect x="320" y="214" width="60" height="6" rx="3" fill="rgba(255,255,255,0.2)"/>
            <rect x="30" y="100" width="310" height="95" rx="14" fill="rgba(255,255,255,0.18)"/>
            <rect x="30" y="100" width="310" height="95" rx="14" stroke="rgba(255,255,255,0.35)" stroke-width="2"/>
            <rect x="340" y="118" width="48" height="77" rx="10" fill="rgba(255,255,255,0.14)"/>
            <rect x="340" y="118" width="48" height="77" rx="10" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>
            <rect x="346" y="124" width="36" height="30" rx="6" fill="rgba(255,255,255,0.35)"/>
            <rect x="50" y="115" width="42" height="32" rx="6" fill="rgba(255,255,255,0.3)"/>
            <rect x="104" y="115" width="42" height="32" rx="6" fill="rgba(255,255,255,0.3)"/>
            <rect x="158" y="115" width="42" height="32" rx="6" fill="rgba(255,255,255,0.3)"/>
            <rect x="212" y="115" width="42" height="32" rx="6" fill="rgba(255,255,255,0.3)"/>
            <rect x="266" y="115" width="42" height="32" rx="6" fill="rgba(255,255,255,0.3)"/>
            <rect x="290" y="153" width="34" height="42" rx="5" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.3)" stroke-width="1.5"/>
            <circle cx="318" cy="175" r="3" fill="rgba(255,255,255,0.6)"/>
            <circle cx="95" cy="195" r="20" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" stroke-width="3"/>
            <circle cx="95" cy="195" r="10" fill="rgba(255,255,255,0.25)"/>
            <circle cx="280" cy="195" r="20" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" stroke-width="3"/>
            <circle cx="280" cy="195" r="10" fill="rgba(255,255,255,0.25)"/>
            <circle cx="360" cy="195" r="20" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" stroke-width="3"/>
            <circle cx="360" cy="195" r="10" fill="rgba(255,255,255,0.25)"/>
            <rect x="382" y="155" width="10" height="16" rx="3" fill="rgba(255,255,200,0.7)"/>
          </svg>

          <div class="bl-d-tagline">
            <h2>{{ currentLang === 'SQ' ? 'Menaxho çdo udhëtim' : 'Manage every ride' }}</h2>
            <p>
              {{
                currentLang === 'SQ'
                  ? 'Gjurmim live · Nxënës · Shofer · Shkolla'
                  : 'Live tracking · Pupils · Drivers · Schools'
              }}
            </p>
          </div>

          <div class="bl-d-features">
            <div class="bl-d-feat">
              <span class="bl-d-feat-icon">&#128205;</span>
              <span>{{ currentLang === 'SQ' ? 'Gjurmim në kohë reale' : 'Real-time tracking' }}</span>
            </div>
            <div class="bl-d-feat">
              <span class="bl-d-feat-icon">&#128101;</span>
              <span>{{ currentLang === 'SQ' ? 'Menaxhim nxënësish' : 'Student management' }}</span>
            </div>
            <div class="bl-d-feat">
              <span class="bl-d-feat-icon">&#128276;</span>
              <span>{{ currentLang === 'SQ' ? 'Njoftime automatike' : 'Automatic notifications' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right form panel -->
      <div class="bl-d-right">
        <!-- Lang toggle top-right -->
        <button class="bl-d-lang-btn" @click="toggleLang">
          <v-icon size="14" color="#5B7D06">mdi-translate</v-icon>
          <span>{{ currentLang }}</span>
        </button>

        <div class="bl-d-form-wrap">
          <div class="bl-d-form-header">
            <h1>{{ currentLang === 'SQ' ? 'Mirë se vini!' : 'Welcome back!' }}</h1>
            <p>{{ currentLang === 'SQ' ? 'Hyni në llogarinë tuaj për të vazhduar' : 'Sign in to your account to continue' }}</p>
          </div>

          <v-form ref="form" v-model="valid" lazy-validation>
            <div class="bl-field-group">
              <label class="bl-label">{{ currentLang === 'SQ' ? 'Email ose Telefon' : 'Email or Phone' }}</label>
              <v-text-field
                v-model="email"
                outlined dense
                :placeholder="$vuetify.breakpoint.smAndDown ? 'email / 044... / +383...' : 'email@shembull.com  ose  044123456  ose  +38344123456'"
                required :rules="emailRules"
                hide-details="auto" class="mb-1"
                prepend-inner-icon="mdi-account-outline"
              ></v-text-field>
            </div>

            <div class="bl-field-group">
              <label class="bl-label">{{ currentLang === 'SQ' ? 'Fjalëkalimi' : 'Password' }}</label>
              <v-text-field
                v-model="password"
                outlined dense
                :type="isPasswordVisible ? 'text' : 'password'"
                placeholder="············"
                :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
                required :rules="passRules"
                hide-details="auto"
                prepend-inner-icon="mdi-lock-outline"
                @click:append="isPasswordVisible = !isPasswordVisible"
              ></v-text-field>
            </div>

            <div class="bl-meta">
              <v-checkbox
                v-model="rememberMe"
                :label="currentLang === 'SQ' ? 'Më mbaj mend' : 'Remember me'"
                hide-details dense class="bl-checkbox"
              />
              <router-link to="/forgot-password" class="bl-forgot">
                {{ currentLang === 'SQ' ? 'Harruat fjalëkalimin?' : 'Forgot password?' }}
              </router-link>
            </div>

            <v-btn block depressed large class="bl-btn mt-5" @click="login">
              <v-icon left size="18">mdi-login</v-icon>
              {{ currentLang === 'SQ' ? 'Hyr' : 'Sign In' }}
            </v-btn>
          </v-form>

          <!-- Demoja hapet ne gjuhen qe eshte zgjedhur ketu. -->
          <router-link v-if="demoEnabled" :to="demoLink" class="bl-demo-btn">
            <span class="bl-demo-icon">&#128269;</span>
            {{ currentLang === 'SQ' ? 'Eksploro versionin demo' : 'Explore the demo version' }}
          </router-link>

          <a href="/app/" class="bl-driver-btn">
            <span class="bl-demo-icon">&#128241;</span>
            <span>
              {{ currentLang === 'SQ' ? 'Shkarko aplikacionin për shoferë' : 'Download the driver app' }}
              <small>{{ currentLang === 'SQ' ? 'Vetëm për Android' : 'Android only' }}</small>
            </span>
          </a>

          <p class="bl-d-footer">
            &copy; {{ new Date().getFullYear() }} {{ systemName }}.
            {{ currentLang === 'SQ' ? 'Të gjitha të drejtat e rezervuara.' : 'All rights reserved.' }}
          </p>
          <div class="bl-d-links">
            <router-link to="/privacy" class="bl-link">{{ currentLang === 'SQ' ? 'Politika e Privatësisë' : 'Privacy Policy' }}</router-link>
            <span class="bl-link-sep">·</span>
            <router-link to="/terms" class="bl-link">{{ currentLang === 'SQ' ? 'Kushtet e Shërbimit' : 'Terms & Conditions' }}</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mdiFacebook, mdiTwitter, mdiGithub, mdiGoogle,
  mdiEyeOutline, mdiEyeOffOutline,
} from '@mdi/js';
import axios from 'axios';
import { ref } from '@vue/composition-api';
import AuthService from '@/services/AuthService';
import { getError } from '@/utils/helpers';
import VueElementLoading from 'vue-element-loading';
import { Keys } from '/src/config.js';
import { loadLocaleMessagesAsync } from '@/i18n';
import { setDocumentLang } from '@/utils/document';

export default {
  components: { VueElementLoading },
  setup() {
    const isPasswordVisible = ref(false);
    const email = ref('');
    const password = ref('');
    return {
      isPasswordVisible, email, password,
      icons: { mdiEyeOutline, mdiEyeOffOutline },
    };
  },
  data() {
    return {
      systemName: Keys.VUE_APP_SYSTEM_NAME,
      // Demoja mund te jete e mbyllur nga serveri; butoni nuk duhet te
      // premtoje dicka qe nuk hapet.
      demoEnabled: false,
      valid: true,
      submiting: false,
      rememberMe: false,
      currentLang: (localStorage.getItem('locale') || 'sq').toUpperCase(),
    };
  },
  computed: {
    // Mesazhet e kontrollit ndjekin gjuhen si cdo tekst tjeter i faqes.
    emailRules() {
      const sq = this.currentLang === 'SQ';
      return [
        (v) =>
          !!v ||
          (sq
            ? 'E-mail ose numri i telefonit është i detyrueshëm'
            : 'Email or phone number is required'),
        (v) => {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const phonePattern = /^\+?[0-9\s\-().]{9,20}$/;
          return (
            emailPattern.test(v) ||
            phonePattern.test(v) ||
            (sq
              ? 'Duhet të jetë email ose telefon i vlefshëm'
              : 'Must be a valid email or phone number')
          );
        },
      ];
    },
    passRules() {
      return [
        (v) =>
          !!v ||
          (this.currentLang === 'SQ'
            ? 'Fjalëkalimi është i detyrueshëm'
            : 'Password is required'),
      ];
    },

    /** Demoja hapet po ne ate gjuhe qe vizitori ka zgjedhur ketu. */
    demoLink() {
      return this.currentLang === 'EN' ? '/demo?lang=en' : '/demo';
    },
  },
  mounted() {
    const savedEmail = localStorage.getItem('remembered_email');
    if (savedEmail) {
      this.email = savedEmail;
      this.rememberMe = true;
    }
    this.kontrolloDemon();
  },
  methods: {
    /** Butoni i demos shfaqet vetem nese serveri e ka te hapur. */
    async kontrolloDemon() {
      try {
        const { data } = await axios.get('/docs/demo-enabled');
        this.demoEnabled = !!(data && data.enabled);
      } catch (e) {
        this.demoEnabled = false;
      }
    },

    toggleLang() {
      const next = this.currentLang === 'SQ' ? 'en' : 'sq';
      localStorage.setItem('locale', next);
      this.currentLang = next.toUpperCase();
      loadLocaleMessagesAsync(next).then(() => setDocumentLang(next));
    },
    validate() {
      return this.$refs.form.validate();
    },
    detectType(input) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const telRegex = /^\+?[0-9\s\-().]{7,}$/;
      if (emailRegex.test(input)) return 'email';
      else if (telRegex.test(input)) return 'telephone';
      else return 'unknown';
    },
    async login() {
      if (!this.validate()) return;
      const type = this.detectType(this.email);
      let email;
      if (type === 'telephone') {
        email = await AuthService.getEmailForTelNumber({
          tel_number: this.email,
          notify: this.$notify,
        });
      }
      const payload = {
        email: email ?? this.email,
        password: this.password,
        notify: this.$notify,
      };
      this.error = null;
      try {
        this.submiting = true;
        const user = await AuthService.login(payload);
        this.submiting = false;
        if (user) {
          if (this.rememberMe) {
            localStorage.setItem('remembered_email', this.email);
          } else {
            localStorage.removeItem('remembered_email');
          }
          const isAdministrator = user.role_id == 1;
          const isSchoolAdmin = user.role_id == 2;
          const isDriver = user.role_id == 3;
          const isParent = user.role_id == 4 || user.role_id == 5;
          const isStudent = user.role_id == 6;
          const isDirector = user.role_id == 7;
          this.$router.push(
            this.$router.currentRoute.query.to || isAdministrator
              ? '/admin-dashboard'
              : isSchoolAdmin
              ? '/school-dashboard'
              : isDirector
              ? '/school-dashboard'
              : isDriver
              ? '/driver/rides'
              : isParent
              ? '/parent/live-tracking'
              : isStudent
              ? '/student/live-tracking'
              : '/error-404'
          );
        }
      } catch (error) {
        this.submiting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Global font ── */
.bl-wrapper, .bl-wrapper * {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}
.bl-wrapper {
  min-height: 100vh;
  min-height: 100dvh; /* iOS 15.4+ dynamic viewport */
  width: 100%;
}

/* ═══════════════════════════════════════
   MOBILE LAYOUT
═══════════════════════════════════════ */
.bl-mobile {
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  background: #f8f9ff;
}

/* Hero top section */
.bl-m-hero {
  background: linear-gradient(150deg, #8B3A10 0%, #E8701E 45%, #F5A030 100%);
  /* Safe area: notch / Dynamic Island top padding */
  padding: calc(48px + env(safe-area-inset-top, 0px)) 28px 80px;
  position: relative;
  overflow: hidden;
}
.bl-m-hero::before {
  content: '';
  position: absolute;
  top: -60px; right: -60px;
  width: 200px; height: 200px;
  border-radius: 50%;
  background: rgba(255,255,255,0.07);
}
.bl-m-hero::after {
  content: '';
  position: absolute;
  bottom: -40px; left: -40px;
  width: 140px; height: 140px;
  border-radius: 50%;
  background: rgba(255,255,255,0.05);
}

/* Language toggle */
.bl-lang-btn {
  position: absolute;
  top: calc(20px + env(safe-area-inset-top, 0px));
  right: 20px;
  display: flex; align-items: center; gap: 5px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 20px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.5px;
  backdrop-filter: blur(4px);
}
.bl-lang-btn:active { opacity: 0.8; }

/* Logo area */
.bl-m-logo-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
}
.bl-m-logo-ring {
  width: 80px; height: 80px;
  background: rgba(255,255,255,0.2);
  border-radius: 24px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 14px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
  border: 2px solid rgba(255,255,255,0.3);
}
.bl-m-app-name {
  font-family: 'Poppins', sans-serif !important;
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.3px;
  margin: 0 0 6px;
}
.bl-m-tagline {
  font-size: 12px;
  color: rgba(255,255,255,0.75);
  letter-spacing: 0.3px;
  margin: 0;
}

/* Form card — overlaps hero with negative margin */
.bl-m-card {
  background: #fff;
  border-radius: 28px 28px 0 0;
  margin-top: -32px;
  flex: 1;
  padding: 24px 24px calc(20px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -4px 24px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
}
.bl-m-card-handle {
  width: 36px; height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 0 auto 20px;
}
.bl-m-greeting {
  text-align: center;
  margin-bottom: 20px;
}
.bl-m-title {
  font-family: 'Poppins', sans-serif !important;
  font-size: 22px;
  font-weight: 800;
  color: #5B7D06;
  margin: 0 0 4px;
}
.bl-m-subtitle {
  font-size: 13px;
  color: #5B7D06;
  margin: 0;
}
.bl-m-form { flex: 1; }
.bl-m-footer {
  text-align: center;
  font-size: 11px;
  color: #6E9707;
  margin: 16px 0 0;
}

/* ═══════════════════════════════════════
   SHARED FORM ELEMENTS
═══════════════════════════════════════ */
.bl-field-group { margin-bottom: 12px; }
.bl-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5B7D06;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}
.bl-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4px 0 0;
}
.bl-forgot {
  font-size: 12px;
  color: #6E9707;
  text-decoration: none;
  font-weight: 600;
}
.bl-forgot:hover { text-decoration: underline; }
.bl-btn {
  background: linear-gradient(90deg, #E8701E, #F5A030) !important;
  color: #fff !important;
  font-family: 'Poppins', sans-serif !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  letter-spacing: 0.5px !important;
  border-radius: 12px !important;
  height: 50px !important;
  box-shadow: 0 4px 16px rgba(230,81,0,0.35) !important;
  margin-top: 16px !important;
}
.bl-btn:hover {
  box-shadow: 0 6px 22px rgba(230,81,0,0.5) !important;
  transform: translateY(-1px);
}

/* ═══════════════════════════════════════
   DESKTOP LAYOUT
═══════════════════════════════════════ */
.bl-desktop {
  min-height: 100vh;
  background: #fff8f0;
}

/* Desktop left */
.bl-d-left {
  width: 52%;
  background: linear-gradient(145deg, #8B3A10 0%, #E8701E 40%, #F08A28 70%, #F5A030 100%);
  display: flex; align-items: center; justify-content: center;
  padding: 48px 40px;
  position: relative; overflow: hidden;
}
.bl-d-left::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20width%3D'240'%20height%3D'240'%20viewBox%3D'0%200%20240%20240'%3E%3Cg%20transform%3D'rotate(-15%2062%2058)'%20stroke%3D'rgba(255%2C255%2C255%2C0.13)'%20stroke-width%3D'3'%20fill%3D'none'%3E%3Crect%20x%3D'25'%20y%3D'35'%20width%3D'74'%20height%3D'40'%20rx%3D'9'%2F%3E%3Crect%20x%3D'33'%20y%3D'43'%20width%3D'13'%20height%3D'11'%20rx%3D'2'%2F%3E%3Crect%20x%3D'51'%20y%3D'43'%20width%3D'13'%20height%3D'11'%20rx%3D'2'%2F%3E%3Crect%20x%3D'69'%20y%3D'43'%20width%3D'13'%20height%3D'11'%20rx%3D'2'%2F%3E%3Ccircle%20cx%3D'43'%20cy%3D'79'%20r%3D'6'%2F%3E%3Ccircle%20cx%3D'83'%20cy%3D'79'%20r%3D'6'%2F%3E%3C%2Fg%3E%3Cg%20transform%3D'rotate(22%20168%20172)'%20stroke%3D'rgba(255%2C255%2C255%2C0.10)'%20stroke-width%3D'3'%20fill%3D'none'%3E%3Crect%20x%3D'134'%20y%3D'150'%20width%3D'64'%20height%3D'35'%20rx%3D'8'%2F%3E%3Crect%20x%3D'141'%20y%3D'157'%20width%3D'11'%20height%3D'9'%20rx%3D'2'%2F%3E%3Crect%20x%3D'157'%20y%3D'157'%20width%3D'11'%20height%3D'9'%20rx%3D'2'%2F%3E%3Crect%20x%3D'173'%20y%3D'157'%20width%3D'11'%20height%3D'9'%20rx%3D'2'%2F%3E%3Ccircle%20cx%3D'149'%20cy%3D'189'%20r%3D'5'%2F%3E%3Ccircle%20cx%3D'184'%20cy%3D'189'%20r%3D'5'%2F%3E%3C%2Fg%3E%3Cg%20transform%3D'rotate(8%20180%2055)'%20stroke%3D'rgba(255%2C255%2C255%2C0.08)'%20stroke-width%3D'2.5'%20fill%3D'none'%3E%3Crect%20x%3D'152'%20y%3D'38'%20width%3D'52'%20height%3D'28'%20rx%3D'7'%2F%3E%3Crect%20x%3D'158'%20y%3D'44'%20width%3D'9'%20height%3D'8'%20rx%3D'2'%2F%3E%3Crect%20x%3D'171'%20y%3D'44'%20width%3D'9'%20height%3D'8'%20rx%3D'2'%2F%3E%3Crect%20x%3D'184'%20y%3D'44'%20width%3D'9'%20height%3D'8'%20rx%3D'2'%2F%3E%3Ccircle%20cx%3D'164'%20cy%3D'69'%20r%3D'4'%2F%3E%3Ccircle%20cx%3D'192'%20cy%3D'69'%20r%3D'4'%2F%3E%3C%2Fg%3E%3Cg%20transform%3D'rotate(-25%2055%20175)'%20stroke%3D'rgba(255%2C255%2C255%2C0.09)'%20stroke-width%3D'2.5'%20fill%3D'none'%3E%3Crect%20x%3D'30'%20y%3D'160'%20width%3D'52'%20height%3D'28'%20rx%3D'7'%2F%3E%3Crect%20x%3D'36'%20y%3D'166'%20width%3D'9'%20height%3D'8'%20rx%3D'2'%2F%3E%3Crect%20x%3D'49'%20y%3D'166'%20width%3D'9'%20height%3D'8'%20rx%3D'2'%2F%3E%3Crect%20x%3D'62'%20y%3D'166'%20width%3D'9'%20height%3D'8'%20rx%3D'2'%2F%3E%3Ccircle%20cx%3D'42'%20cy%3D'191'%20r%3D'4'%2F%3E%3Ccircle%20cx%3D'70'%20cy%3D'191'%20r%3D'4'%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E");
  background-size: 240px 240px;
}
.bl-d-left-inner { position: relative; z-index: 1; width: 100%; max-width: 460px; }
.bl-d-brand {
  display: flex; align-items: center; gap: 12px; margin-bottom: 40px;
}
.bl-d-brand-name {
  color: #fff; font-size: 22px; font-weight: 700;
  font-family: 'Poppins', sans-serif !important;
}
.bl-d-svg {
  width: 100%; max-width: 400px;
  margin: 0 auto 32px; display: block;
  filter: drop-shadow(0 8px 24px rgba(0,0,0,0.25));
}
.bl-d-tagline { color: #fff; margin-bottom: 28px; }
.bl-d-tagline h2 {
  font-family: 'Poppins', sans-serif !important;
  font-size: 28px; font-weight: 800; margin-bottom: 6px; line-height: 1.2;
}
.bl-d-tagline p { font-size: 14px; opacity: 0.75; letter-spacing: 0.5px; }
.bl-d-features { display: flex; flex-direction: column; gap: 10px; }
.bl-d-feat {
  display: flex; align-items: center; gap: 10px;
  color: rgba(255,255,255,0.85); font-size: 14px;
}
.bl-d-feat-icon {
  font-size: 18px; width: 32px; height: 32px;
  background: rgba(255,255,255,0.12); border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
}

/* Desktop right */
.bl-d-right {
  width: 48%;
  display: flex; align-items: center; justify-content: center;
  padding: 40px 32px; background: #FFF6EB;
  position: relative;
}
.bl-d-lang-btn {
  position: absolute;
  top: 20px; right: 24px;
  display: flex; align-items: center; gap: 5px;
  background: rgba(230,81,0,0.08);
  border: 1px solid rgba(230,81,0,0.2);
  border-radius: 20px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 11px; font-weight: 700;
  color: #5B7D06; letter-spacing: 0.5px;
}
.bl-d-lang-btn:hover { background: rgba(91,125,6,0.12); }
.bl-d-form-wrap { width: 100%; max-width: 380px; }
.bl-d-form-header { margin-bottom: 28px; }
.bl-d-form-header h1 {
  font-family: 'Poppins', sans-serif !important;
  font-size: 26px; font-weight: 800; color: #5B7D06; margin-bottom: 4px;
}
.bl-d-form-header p { font-size: 14px; color: #5B7D06; }
.bl-d-footer { text-align: center; color: #7FA62B; font-size: 12px; margin-top: 32px; }

/* Teksti i shkruar: portokallia mbi te bardhe lexohej keq. */
::v-deep input {
  color: #3F5A05 !important;
  -webkit-text-fill-color: #3F5A05 !important;
}
::v-deep input::placeholder {
  color: rgba(91, 125, 6, 0.45) !important;
  -webkit-text-fill-color: rgba(91, 125, 6, 0.45) !important;
}
/* Override browser autofill gray */
::v-deep input:-webkit-autofill,
::v-deep input:-webkit-autofill:hover,
::v-deep input:-webkit-autofill:focus {
  -webkit-text-fill-color: #3F5A05 !important;
  -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
  transition: background-color 5000s ease-in-out 0s;
}
/* Orange border on input fields */
::v-deep .v-text-field--outlined fieldset {
  border-color: #e65100 !important;
}
/* Teksti dytesor ndjek te gjelbren, si etiketat e fushave. */
::v-deep .bl-checkbox .v-label {
  color: #5B7D06 !important;
}
::v-deep .bl-checkbox .v-icon {
  color: #6E9707 !important;
}

.bl-m-links, .bl-d-links { text-align: center; margin-top: 10px; }
.bl-link { color: #7FA62B; font-size: 12px; font-weight: 600; text-decoration: none; transition: color 0.15s; }
.bl-link:hover { color: #5B7D06; text-decoration: underline; }
.bl-link-sep { color: #7FA62B; margin: 0 8px; opacity: 0.6; font-size: 12px; }

/* Ftese, jo veprim kryesor: e vizatuar me konture, qe te mos konkurroje me
   butonin "Hyr". */
.bl-driver-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 18px auto 22px;
  padding: 11px 18px;
  max-width: 320px;
  border: 1.5px solid #86B809;
  border-radius: 10px;
  background: rgba(134, 184, 9, 0.08);
  color: #5B7D06;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.18s, border-color 0.18s;
}
.bl-driver-btn:hover {
  background: rgba(134, 184, 9, 0.16);
  border-color: #6E9707;
}
.bl-driver-btn small {
  display: block;
  font-weight: 500;
  font-size: 11.5px;
  color: #7a9a3f;
}

.bl-demo-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 18px auto 22px;
  padding: 11px 18px;
  max-width: 320px;
  border: 1.5px solid #F0A56B;
  border-radius: 10px;
  background: rgba(255, 247, 240, 0.6);
  color: #E8701E;
  font-size: 13.5px;
  font-weight: 700;
  text-decoration: none;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;
}
.bl-demo-btn:hover {
  background: #E8701E;
  border-color: #E8701E;
  color: #fff;
}
.bl-demo-icon { font-size: 15px; line-height: 1; }
</style>
